import {
  Calendar,
  Close,
  NavigateRight,
} from '@randstad-lean-mobile-factory/react-assets/dist/icons';
import classnames from 'classnames';
import { format, isAfter, isBefore, parse } from 'date-fns';
import { fr } from 'date-fns/locale';
import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Button } from '../../Button/Button';
import { DatePicker } from '../../Calendars';
import { DatePickerActions } from '../../Calendars/DatePicker/DatePicker.types';
import { Menu, MenuActions } from '../../Menus';
import { PositionObserver } from '../../utils';
import { TextInput } from '../TextInput';

import styles from './DatePickerInput.module.scss';
import { DatePickerInputProps } from './DatePickerInput.types';

export const DatePickerInput = forwardRef<DatePickerActions, DatePickerInputProps>(
  ({ size = 'large', isClearable = true, arrow, className, error, ...props }, ref) => {
    const [isOpen, setIsOpen] = useState(false);

    const [inputValue, setInputValue] = useState('');

    const triggerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const menuRef = useRef<MenuActions>(null);

    const [bottom, setBottom] = useState(false);
    const [height, setHeight] = useState(0);
    const updatePosition = useCallback(() => {
      if (isOpen) return;
      const boundingBox = triggerRef.current?.getBoundingClientRect();
      const bottom = window.innerHeight - (boundingBox?.bottom ?? 0) > (boundingBox?.top ?? 0);
      const height =
        (bottom ? window.innerHeight - (boundingBox?.bottom ?? 0) : boundingBox?.top ?? 0) - 16;
      setHeight(height);
      setBottom(bottom);
    }, [isOpen]);
    useLayoutEffect(() => updatePosition());
    useEffect(() => {
      if (!triggerRef.current) return;
      const ob = new PositionObserver(updatePosition, {});
      ob.observe(triggerRef.current);
      return () => ob.disconnect();
    }, [updatePosition]);

    return (
      <Menu
        trigger={
          <div
            tabIndex={props.disabled ? undefined : 0}
            className={classnames(styles.trigger, className, styles[size], {
              [styles.disabled]: props.disabled,
              [styles.error]: error,
            })}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                if (props.disabled) return;
                setIsOpen(true);
                menuRef.current?.open();
              }
            }}
            ref={triggerRef}
          >
            <Calendar color="blue" />
            <div className={styles.triggerContent}>
              {props.selected ? (
                format(
                  props.selected,
                  typeof props.dateFormat === 'string'
                    ? props.dateFormat
                    : props.dateFormat?.at(0) ?? (props.showTimeSelect ? 'Pp' : 'P'),
                  {
                    locale: props.locale ?? fr,
                  }
                )
              ) : (
                <span className={styles.placeholder}>{props.placeholderText}</span>
              )}
            </div>
            {arrow && <NavigateRight color="blue" />}
          </div>
        }
        onOpen={() => {
          setIsOpen(true);
          setInputValue(
            props.selected
              ? format(props.selected, props.showTimeSelect ? 'Pp' : 'P', {
                  locale: props.locale ?? fr,
                })
              : ''
          );
          setTimeout(() => inputRef.current?.focus());
        }}
        onClose={() => {
          setIsOpen(false);
          setInputValue('');
        }}
        ref={menuRef}
        anchorOrigin={{ horizontal: 'left', vertical: bottom ? 'bottom' : 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: bottom ? 'top' : 'bottom' }}
        disabled={props.disabled}
        className={styles.menu}
        // @ts-expect-error ---
        style={{ '--menu-height': height + 'px' }}
      >
        <TextInput
          value={inputValue}
          onValueChange={value => setInputValue(value)}
          autoFocus
          size="medium"
          placeholder={props.showTimeSelect ? 'jj/mm/aaaa, hh:mm' : 'jj/mm/aaaa'}
          leftIcon={<Calendar />}
          rightIcon={
            props.selected && isClearable ? (
              <Button
                variant="tertiary"
                onClick={() => {
                  setInputValue('');
                  props.onChange?.(undefined);
                }}
              >
                <Close />
              </Button>
            ) : null
          }
          onInput={event => {
            const nativeEvent = event.nativeEvent;
            if (!((e): e is InputEvent => e.type === 'input')(nativeEvent)) return;
            if (props.showTimeSelect) {
              let value = event.currentTarget.value
                .replace(/[^\d/,: ]/g, '')
                .replace(/^[/,: ]*/g, '')
                .replace(/\/+/g, '/')
                .replace(/^(\d\d)(\d)$/g, '$1/$2')
                .replace(/^(\d\d\/\d\d\/\d\d\d\d),? ?([\d:])$/g, '$1, $2')
                .replace(/^(\d\d\/\d\d\/\d\d\d\d, \d\d)(\d)$/g, '$1:$2');
              if (nativeEvent.inputType === 'insertText')
                value = value
                  .replace(/^(\d\d)$/g, '$1/')
                  .replace(/^(\d\d\/\d\d)$/g, '$1/')
                  .replace(/^(\d\d\/\d\d\/\d\d\d\d)$/g, '$1, ')
                  .replace(/^(\d\d\/\d\d\/\d\d\d\d, \d\d)$/g, '$1:');
              event.currentTarget.value = value;
            } else {
              let value = event.currentTarget.value
                .replace(/[^\d/]/g, '')
                .replace(/^\/*/g, '')
                .replace(/\/+/g, '/')
                .replace(/^(\d\d)(\d+)$/g, '$1/$2')
                .replace(/^(\d\d\/\d\d)(\d+)$/g, '$1/$2');
              if (nativeEvent.inputType === 'insertText')
                value = value.replace(/^(\d\d)$/g, '$1/').replace(/^(\d\d\/\d\d)$/g, '$1/');
              event.currentTarget.value = value;
            }
          }}
          onChange={event => {
            const value = event.currentTarget.value;
            setInputValue(value);
            let date: Date | undefined = parse(
              value,
              props.showTimeSelect ? 'Pp' : 'P',
              props.selected ?? 0,
              { locale: props.locale ?? fr }
            );
            if (isNaN(date.getDate())) date = undefined;
            if (date && props.minDate && isBefore(date, props.minDate)) date = undefined;
            if (date && props.maxDate && isAfter(date, props.maxDate)) date = undefined;
            if (date || isClearable) props.onChange?.(date);
          }}
          onBlur={() =>
            setInputValue(
              props.selected
                ? format(props.selected, props.showTimeSelect ? 'Pp' : 'P', {
                    locale: props.locale ?? fr,
                  })
                : ''
            )
          }
          onKeyDown={event => {
            if (event.key === 'Enter') {
              setInputValue('');
              menuRef.current?.close();
            }
          }}
          containerClassName={styles.input}
          ref={inputRef}
        />
        <DatePicker
          {...props}
          onSelect={() => {
            if (props.shouldCloseOnSelect) setIsOpen(false);
          }}
          ref={ref}
        />
      </Menu>
    );
  }
);
